/**
 * Push Notification Manager
 * Handles service worker registration, push subscriptions, and permission management
 *
 * NOTE: This uses the standard Web Push API - NO Firebase/FCM required!
 * We're using pywebpush on the backend which is a Python library for sending Web Push notifications.
 */

const SW_URL = '/push-notification-sw.js';

// You'll need to generate VAPID keys on backend and provide the public key here
// For now, using a placeholder - REPLACE THIS WITH YOUR ACTUAL VAPID PUBLIC KEY
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || 'REPLACE_WITH_YOUR_VAPID_PUBLIC_KEY';

/**
 * Convert VAPID key from base64 to Uint8Array
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Check if push notifications are supported
 */
export function isPushNotificationSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

/**
 * Check current permission status
 */
export function getNotificationPermission() {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}

/**
 * Request notification permission from user
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    console.warn('This browser does not support notifications');
    return 'unsupported';
  }

  const permission = await Notification.requestPermission();
  console.log('Notification permission:', permission);
  return permission;
}

/**
 * Register the service worker
 */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service Workers are not supported in this browser');
  }

  try {
    // Check if service worker is already registered
    const existingRegistration = await navigator.serviceWorker.getRegistration();

    if (existingRegistration) {
      console.log('Service Worker already registered');
      await navigator.serviceWorker.ready;
      return existingRegistration;
    }

    // Register new service worker
    const registration = await navigator.serviceWorker.register(SW_URL, {
      scope: '/'
    });

    console.log('Service Worker registered successfully:', registration);

    // Wait for the service worker to be ready
    await navigator.serviceWorker.ready;

    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    throw error;
  }
}

/**
 * Get existing push subscription
 */
export async function getExistingSubscription() {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription;
  } catch (error) {
    console.error('Error getting existing subscription:', error);
    return null;
  }
}

/**
 * Subscribe to push notifications
 */
export async function subscribeToPushNotifications() {
  try {
    // Check if already subscribed
    const existingSubscription = await getExistingSubscription();
    if (existingSubscription) {
      console.log('Already subscribed to push notifications');
      return existingSubscription;
    }

    // Get service worker registration
    const registration = await navigator.serviceWorker.ready;

    // Convert VAPID key
    const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);

    // Subscribe to push manager
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey
    });

    console.log('Successfully subscribed to push notifications:', subscription);
    return subscription;
  } catch (error) {
    console.error('Failed to subscribe to push notifications:', error);
    throw error;
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPushNotifications() {
  try {
    const subscription = await getExistingSubscription();

    if (subscription) {
      const successful = await subscription.unsubscribe();
      console.log('Unsubscribed from push notifications:', successful);
      return successful;
    }

    return false;
  } catch (error) {
    console.error('Failed to unsubscribe from push notifications:', error);
    throw error;
  }
}

/**
 * Initialize push notifications (full flow)
 * 1. Request permission
 * 2. Register service worker
 * 3. Subscribe to push notifications
 * 4. Send subscription to backend
 */
export async function initializePushNotifications(sendSubscriptionToBackend) {
  try {
    // Check support
    if (!isPushNotificationSupported()) {
      throw new Error('Push notifications are not supported in this browser');
    }

    // Check existing permission
    let permission = getNotificationPermission();

    // If not granted, request permission
    if (permission === 'default') {
      permission = await requestNotificationPermission();
    }

    // If permission denied, cannot proceed
    if (permission === 'denied') {
      throw new Error('Notification permission denied by user');
    }

    // If permission granted, proceed with subscription
    if (permission === 'granted') {
      // Register service worker
      await registerServiceWorker();

      // Subscribe to push notifications
      const subscription = await subscribeToPushNotifications();

      // Send subscription to backend
      if (sendSubscriptionToBackend && typeof sendSubscriptionToBackend === 'function') {
        await sendSubscriptionToBackend(subscription);
      }

      return {
        success: true,
        subscription,
        permission
      };
    }

    return {
      success: false,
      permission
    };
  } catch (error) {
    console.error('Error initializing push notifications:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Show a test notification (for testing purposes)
 */
export async function showTestNotification() {
  try {
    // Check if notifications are supported
    if (!('Notification' in window)) {
      throw new Error('This browser does not support notifications');
    }

    // Check permission
    let permission = getNotificationPermission();

    // If permission not granted, request it
    if (permission === 'default') {
      permission = await requestNotificationPermission();
    }

    if (permission !== 'granted') {
      throw new Error('Notification permission not granted');
    }

    // Check if service worker is registered, if not register it
    let registration;
    if ('serviceWorker' in navigator) {
      const existingRegistration = await navigator.serviceWorker.getRegistration();

      if (!existingRegistration) {
        console.log('Service worker not registered, registering now...');
        registration = await registerServiceWorker();
      } else {
        registration = existingRegistration;
        await navigator.serviceWorker.ready;
      }
    } else {
      throw new Error('Service Workers are not supported in this browser');
    }

    // Show the notification
    await registration.showNotification('Test Notification', {
      body: 'This is a test notification from CRM',
      icon: '/logo192.png',
      badge: '/logo192.png',
      tag: 'test-notification-' + Date.now(),
      data: {
        url: '/',
        timestamp: Date.now()
      },
      requireInteraction: false,
      silent: false,
      vibrate: [200, 100, 200]
    });

    console.log('Test notification sent successfully');
    return true;
  } catch (error) {
    console.error('Error showing test notification:', error);
    throw error;
  }
}

/**
 * Get subscription info in JSON format
 * Returns flattened structure for backend API
 */
export function getSubscriptionJSON(subscription) {
  if (!subscription) return null;

  const subscriptionJSON = subscription.toJSON();

  return {
    endpoint: subscriptionJSON.endpoint,
    p256dh: subscriptionJSON.keys.p256dh,
    auth: subscriptionJSON.keys.auth
  };
}

/**
 * Detect browser type for device tracking
 */
export function getBrowserType() {
  const userAgent = navigator.userAgent.toLowerCase();

  if (userAgent.includes('chrome') && !userAgent.includes('edg')) return 'chrome';
  if (userAgent.includes('firefox')) return 'firefox';
  if (userAgent.includes('safari') && !userAgent.includes('chrome')) return 'safari';
  if (userAgent.includes('edg')) return 'edge';
  if (userAgent.includes('opera') || userAgent.includes('opr')) return 'opera';

  return 'unknown';
}

const pushNotifications = {
  isPushNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  registerServiceWorker,
  getExistingSubscription,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  initializePushNotifications,
  showTestNotification,
  getSubscriptionJSON,
  getBrowserType
};

export default pushNotifications;
